import React, { useState, useEffect, useRef } from "react";
import ChartComponent from "./Charts/ChartComponent";
import NewsFeed from "./NewsFeed/NewsFeed";
import EmgptDiv from "./Emgpt/EmgptDiv";
import { Navigate, useLocation } from "react-router-dom";
import Switch from "react-switch";
import {
  getDefaultDates,
  initializeFilters,
  renderLoadingOrNoData,
  downloadDashboardData,
  extractTopSentiments,
  fetchDashboardFilter,
  fetchDashboardData,
  getStoredSelectedFilters,
} from "./DashboardUtils";
import {
  DownloadDashboardDataRequest,
  FetchDashboardDataRequest,
  FetchDashboardFiltersRequest,
} from "./DashboardSerializer";
import { loadMoreNews, loadNews, loadNewsPointClick } from "./NewsFeed/NewsFeedUtils";
import { Helmet } from "react-helmet";
import ProductDashboardNoSubscription from "./ProductDashboardNoSubscription";
import { Button, Offcanvas, DropdownButton, Dropdown } from "react-bootstrap";
import { FaBars } from "react-icons/fa";
import TopSentimentSummary from "./Charts/TopSentimentSummary";
import MobileHeader from "../CoTrader/MobileHeader";
import FilterBarMobile from "./FilterBar/FilterBarMobile";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import {
  buildDashboardRoute,
  resolveDashboardNameFromSearch,
} from "../../utilities/dashboardRouteUtils";
import DashboardInfoButton from "./DashboardInfoButton";
import useDashboardPriceOverlay from "./useDashboardPriceOverlay";
import { getUserId } from "../../utilities/authHelper";
import ChartLoader from "../Common/ChartLoader";

function ProductDashboardMobile({ subTabs }) {
  const dashboardSubtitle = "AI Agent-Powered Insights";
  const location = useLocation();
  const dashboard_name = resolveDashboardNameFromSearch(subTabs, location.search);

  // ===== Theme (localStorage + event) =====
  
  const {isDark} = useTheme(); 

  const t = isDark
    ? {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        surface2: "#0F1424",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        primary: "#4C7DFF",
        primarySoft: "rgba(76,125,255,0.14)",
      }
    : {
        pageBg: "#FFFFFF",
        surface: "#FFFFFF",
        surface2: "#FFFFFF",
        border: "#E9ECEF",
        text: "#0B1220",
        muted: "#5B6472",
        primary: "#0D6EFD",
        primarySoft: "rgba(13,110,253,0.10)",
      };

  const dashboardKey = dashboard_name || "default_dashboard";
  const localStorageKey = `selectedFilters_${dashboardKey}`;
  const priceOverlayStorageKey = `product-dashboard-price-overlay_${
    getUserId() || "anonymous"
  }_${dashboardKey}`;
  const currentReqKeyRef = useRef(dashboard_name);
  // Each kept-alive tab mounts already pointed at its dashboard; this lets the
  // "dashboard changed" reset skip mount, where it only duplicated fetches.
  const lastDashboardNameRef = useRef(dashboard_name);
  const filterReqSeqRef = useRef(0);
  const dataReqSeqRef = useRef(0);

  const {
    defaultStartDate,
    defaultEndDate,
    default_embedding,
    start_date_limit,
    end_date_limit,
    ner_relevance_cutoff,
    dashboard_description,
    enable_price_overlay,
    point_click_news_lookback_days,
    default_granularity,
    available_granularities,
  } = getDefaultDates(subTabs, dashboard_name);

  const [filterDate, setFilterDate] = useState({
    endDate: defaultEndDate,
    startDate: defaultStartDate,
  });

  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardChartData, setDashboardChartData] = useState(null);

  const [newsList, setNewsList] = useState([]);
  const [isNewsLoading, setIsNewsLoading] = useState(false);
  const [isMoreNewsLoading, setIsMoreNewsLoading] = useState(false);
  const [noMoreNews, setNoMoreNews] = useState(false);
  const [documentIds, setDocumentIds] = useState([]);
  const [offset, SetOffset] = useState(0);

  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [isEmgptLoading, setIsEmgptLoading] = useState(false);
  const [hasAskedSummarizePrompt, setHasAskedSummarizePrompt] = useState(false);

  const [showSuggestions, setShowSuggestions] = useState(true);
  const [searchFlag, setSearchFlag] = useState(false);

  const [activeBottomTab, setActiveBottomTab] = useState("Info Flow");
  const [showMenuSheet, setShowMenuSheet] = useState(false);

  const [topPositiveSentiments, setTopPositiveSentiments] = useState([]);
  const [topNegativeSentiments, setTopNegativeSentiments] = useState([]);
  const filterBarRef = useRef();
  const isinRef = useRef(null);

  const [isStopped, setIsStopped] = useState(false);
  const isStoppedRef = useRef(isStopped);
  const wsRef = useRef(null);

  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [tabs, setTabs] = useState([]);
  const [tvTickers, setTvTickers] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState(() =>
    getStoredSelectedFilters(localStorageKey)
  );

  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);
  const [dashboardGranularity, setDashboardGranularity] = useState({
    value: default_granularity,
    label: default_granularity,
  });
  const [availableGranularities, setAvailableGranularities] = useState(
    available_granularities
  );
  const {
    canShowPriceOverlay,
    isPriceOverlayLoading,
    priceOverlay,
    priceOverlayTicker,
    showPriceOverlay,
    setShowPriceOverlay,
  } = useDashboardPriceOverlay({
    enabled: enable_price_overlay,
    dashboardChartData,
    filterDate,
    storageKey: priceOverlayStorageKey,
  });

  useEffect(() => {
    if (
      selectedFilters &&
      selectedFilters.filter1 &&
      selectedFilters.filter2?.length > 0 &&
      selectedFilters.filter3?.length > 0
    ) {
      localStorage.setItem(localStorageKey, JSON.stringify(selectedFilters));
    }
    setSelectedPoint(null);
  }, [selectedFilters]);

  useEffect(() => {
    const requestSeq = ++filterReqSeqRef.current;
    const run = async () => {
      if (dashboard_name !== null) {
        await fetchDashboardFilter({
          fetchDashboardFiltersRequest: new FetchDashboardFiltersRequest(
            dashboard_name,
            filterDate
          ),
          setFilters,
          setSelectedFilters,
          selectedFilters,
          setIsLoading,
          currentReqKeyRef,
          localStorageKey,
          isLatestRequest: () => requestSeq === filterReqSeqRef.current,
        });
      } else {
        setIsLoading(false);
      }
    };
    run();
  }, [filterDate]);

  useEffect(() => {
    if (lastDashboardNameRef.current === dashboard_name) return;
    lastDashboardNameRef.current = dashboard_name;
    currentReqKeyRef.current = dashboard_name;
    setDashboardGranularity({ value: default_granularity, label: default_granularity });
    setAvailableGranularities(available_granularities);

    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }

    if (dashboard_name !== null) {
      initializeFilters(
        setDashboardData,
        setFilters,
        setDashboardChartData,
        setNewsList,
        setDocumentIds,
        SetOffset
      );

      const { defaultStartDate, defaultEndDate } = getDefaultDates(subTabs, dashboard_name);
      setFilterDate({ endDate: defaultEndDate, startDate: defaultStartDate });

      setSelectedFilters(getStoredSelectedFilters(localStorageKey));
    }
  }, [dashboard_name]);

  useEffect(() => {
    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }

    const requestSeq = ++dataReqSeqRef.current;
    const isLatestRequest = () => requestSeq === dataReqSeqRef.current;
    const runUpdate = async () => {
      if (selectedFilters.filter1 && selectedFilters.filter2.length > 0) {
        await fetchDashboardData({
          fetchDashboardDataRequest: new FetchDashboardDataRequest(
            dashboard_name,
            filterDate,
            selectedFilters.filter2.map((x) => x.value),
            selectedFilters.filter3.map((x) => x.value),
            dashboardGranularity.value
          ),
          selectedFilters,
          setDashboardData,
          setIsLoading,
          currentReqKeyRef,
          setDashboardChartData,
          filterDate,
          isLatestRequest,
        });

        const { topPositives, topNegatives } = await extractTopSentiments(
          dashboard_name,
          selectedFilters.filter1.value
        );
        if (!isLatestRequest()) return;
        setTopPositiveSentiments(topPositives);
        setTopNegativeSentiments(topNegatives);
      }
    };

    runUpdate();
  }, [selectedFilters, filters, dashboardGranularity]);

  useEffect(() => {
    isinRef.current = dashboardChartData;
    if (dashboardChartData) {
      loadNews(
        dashboardChartData,
        setNewsList,
        setIsNewsLoading,
        filterDate,
        setDocumentIds,
        SetOffset,
        setNoMoreNews,
        isinRef
      );
    }
  }, [dashboardChartData]);

  const handleLoadMoreNews = () => {
    loadMoreNews({
      dashboardChartData,
      offset,
      setIsMoreNewsLoading,
      setNewsList,
      SetOffset,
      filterDate,
      setNoMoreNews,
      setDocumentIds,
      documentIds,
      newsList,
    });
  };

  const handlePointClick = (data) => {
    setSelectedDate(data.category);
    setSelectedPoint(dashboardChartData.filter((x) => x.isin === data.options.isin)[0]);
    loadNewsPointClick(
      [data.options.isin],
      setNewsList,
      setIsNewsLoading,
      data.category,
      setDocumentIds,
      setNoMoreNews,
      default_granularity,
      point_click_news_lookback_days
    );
    setActiveBottomTab("Info Flow");
  };

  const resetDate = () => {
    setSelectedDate(null);
    setSelectedPoint(null);
    loadNews(
      dashboardChartData,
      setNewsList,
      setIsNewsLoading,
      filterDate,
      setDocumentIds,
      SetOffset,
      setNoMoreNews,
      isinRef
    );
  };

  const handleDownload = (format) => {
    let filterCountries = selectedFilters.filter2.map((x) => x.value);
    let filterCompanies = selectedFilters.filter3.map((x) => x.value);
    downloadDashboardData({
      fetchDashboardDataRequest: new DownloadDashboardDataRequest(
        dashboard_name,
        filterDate,
        format,
        filterCountries,
        filterCompanies
      ),
      setIsLoading: setDownloadButtonLoading,
    });
  };

  const dashboardName = subTabs
    .filter((x) => x.dashboardName === dashboard_name)
    .map((x) => x.name);

  useEffect(() => {
    setHasAskedSummarizePrompt(false);
  }, [dashboard_name, dashboardName[0]]);

  const stopWsAndClearChat = () => {
    if (wsRef.current) {
      setIsStopped(true);
      isStoppedRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
      setMessages([]);
      setDynamicSuggestions([]);
    }
  };

  const resetLayoutMobile = () => {
    stopWsAndClearChat();
    setShowMenuSheet(false);
  };

  if (dashboard_name === null && subTabs?.length > 0) {
    return (
      <Navigate
        to={buildDashboardRoute(subTabs[0].dashboardName)}
        replace
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | EMAlpha</title>
      </Helmet>

      {/* global container themed */}
      <div
        className="d-flex flex-column w-100"
        style={{ height: "100dvh", background: t.pageBg }}
      >
        {dashboard_name === null ? (
          <ProductDashboardNoSubscription />
        ) : (
          <>
            {/* FILTER BAR */}
            <div style={{ position: "sticky", top: 0, zIndex: 50 }}>
              <MobileHeader />

              <div style={{ height: "44px", background: t.surface }}>
                <FilterBarMobile
                  ref={filterBarRef}
                  filters={filters}
                  filterDate={filterDate}
                  setFilterDate={setFilterDate}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  isLoading={isLoading}
                  dashboardChartData={dashboardChartData}
                  dashboard_name={dashboardName[0]}
                  start_date_limit={start_date_limit}
                  end_date_limit={end_date_limit}
                  setDashboardGranularity={setDashboardGranularity}
                  dashboardGranularity={dashboardGranularity}
                  availableGranularities={availableGranularities}
                  showSuggestions={showSuggestions}
                  setShowSuggestions={setShowSuggestions}
                  searchFlag={searchFlag}
                  setSearchFlag={setSearchFlag}
                  downloadButtonLoading={downloadButtonLoading}
                  onDownload={handleDownload}
                  isDark={isDark} // ✅ pass down if you want to theme FilterBarMobile too
                />
              </div>
            </div>

            {/* HEADER (title + menu) */}
            <div
              className="px-3 py-2 d-flex align-items-center justify-content-between"
              style={{
                position: "sticky",
                top: 44,
                zIndex: 40,
                background: t.surface,
                borderBottom: `1px solid ${t.border}`,
              }}
            >
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="fw-semibold"
                    style={{ fontSize: "1rem", color: t.text, lineHeight: 1.2 }}
                  >
                    {dashboardName[0] || "Dashboard"}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: t.muted,
                      fontWeight: 500,
                      lineHeight: 1.2,
                      marginTop: "0.2rem",
                    }}
                  >
                    {dashboardSubtitle}
                  </div>
                </div>
                <DashboardInfoButton
                  dashboardName={dashboardName[0] || "Dashboard"}
                  description={dashboard_description}
                  isDark={isDark}
                />
              </div>

              <Button
                className="p-2 d-flex align-items-center"
                onClick={() => setShowMenuSheet(true)}
                style={{
                  background: "transparent",
                  border: `1px solid ${t.border}`,
                  color: t.text,
                }}
              >
                <FaBars size={18} />
              </Button>
            </div>

            {/* BODY */}
            <div
              className="d-flex flex-column flex-grow-1"
              style={{ minHeight: 0, background: t.pageBg }}
            >
              {renderLoadingOrNoData(isLoading, dashboardChartData, dashboardName[0]) || (
                <>
                  {/* CHART */}
                  <div
                    style={{
                      flex: "0 0 30%",
                      minHeight: 240,
                      borderBottom: `1px solid ${t.border}`,
                      background: t.surface,
                    }}
                  >
                    <div style={{ height: "100%", width: "100%", position: "relative" }}>
                      {isPriceOverlayLoading && (
                        <ChartLoader dim maxMs={500} isDark={isDark} label="Loading price history" />
                      )}
                      <ChartComponent
                        dashboardName={dashboard_name}
                        dashboardChartData={dashboardChartData}
                        legends={true}
                        handlePointClick={handlePointClick}
                        isDark={isDark} // ✅ add support inside ChartComponent to use dark background/axis
                        priceOverlay={priceOverlay}
                      />
                    </div>
                  </div>

                  {/* TOP SENTIMENT SUMMARY */}
                  {topPositiveSentiments.length > 0 && topNegativeSentiments.length > 0 && (
                    <div style={{ borderBottom: `1px solid ${t.border}`, background: t.surface }}>
                      <TopSentimentSummary
                        positives={topPositiveSentiments}
                        negatives={topNegativeSentiments}
                        onSentimentClick={(company, country) =>
                          filterBarRef.current?.handleSentimentClick(company, country)
                        }
                        isDark={isDark} // ✅ optional
                      />
                    </div>
                  )}

                  {/* BOTTOM TABS */}
                  <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
                    <div
                      className="px-3 py-2 d-flex gap-2"
                      style={{ borderBottom: `1px solid ${t.border}`, background: t.surface }}
                    >
                      <button
                        className="btn btn-sm rounded-pill"
                        style={{
                          background: activeBottomTab === "Info Flow" ? t.primary : "transparent",
                          border: `1px solid ${
                            activeBottomTab === "Info Flow" ? t.primary : t.border
                          }`,
                          color: activeBottomTab === "Info Flow" ? "#fff" : t.text,
                        }}
                        onClick={() => setActiveBottomTab("Info Flow")}
                      >
                        Info Flow
                      </button>

                      <button
                        className="btn btn-sm rounded-pill"
                        style={{
                          background: activeBottomTab === "emgpt" ? t.primary : "transparent",
                          border: `1px solid ${
                            activeBottomTab === "emgpt" ? t.primary : t.border
                          }`,
                          color: activeBottomTab === "emgpt" ? "#fff" : t.text,
                        }}
                        onClick={() => setActiveBottomTab("emgpt")}
                      >
                        EM GPT
                      </button>
                    </div>

                    <div className="flex-grow-1" style={{ minHeight: 0 }}>
                      {activeBottomTab === "Info Flow" ? (
                        <div
                          className="h-100 overflow-auto p-4"
                          style={{
                            WebkitOverflowScrolling: "touch",
                            background: t.pageBg,
                          }}
                        >
                          <NewsFeed
                            newsList={newsList}
                            isNewsLoading={isNewsLoading}
                            noMoreNews={noMoreNews}
                            setDocumentIds={setDocumentIds}
                            documentIds={documentIds}
                            handleLoadMoreNews={handleLoadMoreNews}
                            isMoreNewsLoading={isMoreNewsLoading}
                            ner_relevance_cutoff={ner_relevance_cutoff}
                            selectedNews={selectedNews}
                            setSelectedNews={setSelectedNews}
                            resetDate={resetDate}
                            selectedPoint={selectedPoint}
                            selectedDate={selectedDate}
                            isDark={isDark} // ✅ optional if you theme NewsFeed tiles
                          />
                        </div>
                      ) : (
                        <div className="h-100" style={{ minHeight: 0 }}>
                          <EmgptDiv
                            documentIds={documentIds}
                            dashboardName={dashboard_name}
                            dashboardTitle={dashboardName[0]}
                            priceTicker={priceOverlayTicker}
                            showPriceOverlay={showPriceOverlay}
                            selectedFilters={selectedFilters}
                            reference_ids={
                              dashboardChartData ? dashboardChartData.map((x) => x.isin) : []
                            }
                            searchFlag={searchFlag}
                            setSearchFlag={setSearchFlag}
                            default_embedding={default_embedding}
                            messages={messages}
                            setMessages={setMessages}
                            userInput={userInput}
                            setUserInput={setUserInput}
                            isLoading={isEmgptLoading}
                            setIsLoading={setIsEmgptLoading}
                            showSuggestions={showSuggestions}
                            setDynamicSuggestions={setDynamicSuggestions}
                            dynamicSuggestions={dynamicSuggestions}
                            isStopped={isStopped}
                            setIsStopped={setIsStopped}
                            isStoppedRef={isStoppedRef}
                            wsRef={wsRef}
                            selectedNews={selectedNews}
                            newsList={newsList}
                            tabs={tabs}
                            setTabs={setTabs}
                            setTvTickers={setTvTickers}
                            selectedDate={selectedDate}
                            selectedPoint={selectedPoint}
                            point_click_news_lookback_days={point_click_news_lookback_days}
                            resetDate={resetDate}
                            isDark={isDark} // ✅ if you theme EmgptDiv bubbles/input
                            hasAskedSummarizePrompt={hasAskedSummarizePrompt}
                            setHasAskedSummarizePrompt={setHasAskedSummarizePrompt}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* SETTINGS SHEET */}
            <Offcanvas
              show={showMenuSheet}
              onHide={() => setShowMenuSheet(false)}
              placement="bottom"
              style={{
                height: "75%",
                background: t.pageBg,
                borderTop: `1px solid ${t.border}`,
              }}
            >
              <Offcanvas.Header
                closeButton
                style={{ background: t.surface, borderBottom: `1px solid ${t.border}` }}
              >
                <Offcanvas.Title style={{ color: t.text }}>Settings</Offcanvas.Title>

                {/* make close icon visible in dark */}
                <style>{`
                  .offcanvas-header .btn-close { filter: ${isDark ? "invert(1)" : "none"}; }
                `}</style>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center" style={{ color: t.text }}>
                      <i className="fa fa-lightbulb me-2" />
                      Show Suggestions
                    </span>
                    <Switch
                      onChange={() => setShowSuggestions(!showSuggestions)}
                      checked={showSuggestions}
                      height={20}
                      width={40}
                      offColor={isDark ? "#232B4A" : "#E9ECEF"}
                      onColor={t.primary}
                      uncheckedIcon={false}
                      checkedIcon={false}
                    />
                  </div>


                  {enable_price_overlay && (
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <div className="d-flex flex-column">
                        <span className="d-flex align-items-center" style={{ color: t.text }}>
                          <i className="fa fa-chart-line me-2" />
                          Show Price Overlay
                        </span>
                        {!canShowPriceOverlay && (
                          <small style={{ color: t.muted, marginLeft: "1.25rem" }}>
                            Select one line with a price ticker.
                          </small>
                        )}
                        {isPriceOverlayLoading && (
                          <small style={{ color: t.muted, marginLeft: "1.25rem" }}>
                            Loading price history…
                          </small>
                        )}
                      </div>
                      <Switch
                        onChange={setShowPriceOverlay}
                        checked={showPriceOverlay}
                        disabled={!canShowPriceOverlay}
                        height={20}
                        width={40}
                        offColor={isDark ? "#232B4A" : "#E9ECEF"}
                        onColor={t.primary}
                        uncheckedIcon={false}
                        checkedIcon={false}
                      />
                    </div>
                  )}

                  <Button
                    onClick={resetLayoutMobile}
                    style={{
                      background: "transparent",
                      border: `1px solid ${isDark ? "#7a1f2a" : "#dc3545"}`,
                      color: isDark ? "#ff8b9b" : "#dc3545",
                    }}
                  >
                    Reset (Mobile)
                  </Button>

                  <hr style={{ borderColor: t.border }} />

                  <DropdownButton
                    id="download-dropdown"
                    title="⬇️ Download Data"
                    disabled={downloadButtonLoading}
                    className="w-100"
                    variant="primary"
                  >
                    {["CSV", "JSON", "XLSX"].map((format) => (
                      <Dropdown.Item key={format} onClick={() => handleDownload(format)}>
                        {format}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>

                  <Button
                    onClick={() => setShowMenuSheet(false)}
                    style={{
                      background: isDark ? t.surface2 : "#F8F9FA",
                      border: `1px solid ${t.border}`,
                      color: t.text,
                    }}
                  >
                    Close
                  </Button>
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          </>
        )}
      </div>
    </>
  );
}

export default ProductDashboardMobile;
